import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useRef,
} from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import './AllVisits.css';
import Subheader from '../../components/subheader/Subheader';
import VisitCard from '../../components/card/VisitCard';
import { UserContext } from '../../contexts/UserContext';
import { getAuthConfig, getApiBaseUrl } from '../../services/configService';
import { useOutletContext } from 'react-router-dom';

const DEFAULT_QUERY_PARAMS = {
  page: 1,
  filterField: null,
  filterValue: '',
  sortBy: '',
  sortOrder: 'asc',
  hospitalFilter: [],
  labelFilter: [],
  statusFilter: [],
  boardStatusFilter: [],
  expiringOnly: false,
  hideClean: false,
  open30plus: false,
};
const POLL_INTERVAL_MS = 5 * 60 * 1000;

const getLimitForViewport = () => {
  const width = window.innerWidth;

  if (width >= 2337) return 20;   // 4K+
  if (width >= 2000) return 16;   // large desktops / ultrawide
  if (width >= 1705) return 12;   // 1080p (your current sweet spot)
  if (width >= 1200) return 10;   // smaller laptops
  return 8;                       // fallback
};

const AllVisits = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    queryParams,
    setQueryParams,
    setLabelList,
    setBoardStatusList,  
  } = useOutletContext();

  const { user } = useContext(UserContext);

  console.log(user.userPrincipalName);
  console.log(user.revenueIds);
  console.log(user.roles);

  const { instance, accounts } = useMsal();

  const [visitData, setVisitData] = useState([]);
  const [limit, setLimit] = useState(getLimitForViewport);
  const [total, setTotal] = useState(0);
  const [hospitalList, setHospitalList] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (location.state?.forceFilters) return;
  
    const params = Object.fromEntries([...searchParams]);
  
    setQueryParams({
      page: Number(params.page) || 1,
      filterField: params.filterField || null,
      filterValue: params.filterValue || '',
      sortBy: params.sortBy || '',
      sortOrder: params.sortOrder || 'asc',
      hospitalFilter: params.hospitalFilter
        ? params.hospitalFilter.split(',')
        : [],
      labelFilter: params.labelFilter
        ? params.labelFilter.split(',')
        : [],
      statusFilter: params.statusFilter
        ? params.statusFilter.split(',')
        : [],
      boardStatusFilter: params.boardStatusFilter
        ? params.boardStatusFilter.split(',')
        : [],
      expiringOnly: params.expiringOnly === 'true',
      hideClean: params.hideClean === 'true',
      open30plus: params.open30plus === 'true',
    });
  
  }, [location.state]); 

  useEffect(() => {
    if (location.state?.forceFilters) {
      setQueryParams({
        ...DEFAULT_QUERY_PARAMS,
        ...location.state.forceFilters,
        page: 1,
      });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state]);
  
  const authConfig = getAuthConfig();

  useEffect(() => {
    const handleResize = () => {
      setLimit(prev => {
        const next = getLimitForViewport();
        return prev !== next ? next : prev;
      });
    };
  
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => { 
    const params = {};
  
    Object.entries(queryParams).forEach(([key, value]) => {
      if (
        value !== null &&
        value !== '' &&
        (!Array.isArray(value) || value.length > 0)
      ) {
        params[key] = Array.isArray(value) ? value.join(',') : value;
      }
    });
  
    setSearchParams(params, { replace: true });
  }, [queryParams, setSearchParams]);  

  const logAutoRefresh = useCallback((type, payload = {}) => {
    const ts = new Date().toISOString();
    console.log(
      `%c[AutoRefresh][${type}] ${ts}`,
      'color:#2563eb;font-weight:600;',
      payload
    );
  }, []);


  const getVisitSignature = useCallback(visit => {
    const labels = (visit.label || [])
      .slice()
      .sort((a, b) => a.rule_id.localeCompare(b.rule_id))
      .map(l => `${l.rule_id}:${l.price}:${l.priority}`)
      .join(',');

    return [
      visit.status,
      visit.invoice_total,
      visit.total_loss,
      visit.percent_loss,
      visit.ready_to_go,
      visit.has_medical_notes,
      visit.charge_capture_total,
      visit.missed_charge_amount,
      visit.failed_discounts,
      visit.total_discounts,
      visit.check_in_at,
      labels,
    ].join('|');
  }, []);


  const getAccessToken = async () => {
    if (!accounts.length) return null;

    try {
      const res = await instance.acquireTokenSilent({
        account: accounts[0],
        scopes: [`api://${authConfig.clientId}/access_as_user`],
      });
      return res.accessToken;
    } catch {
      const res = await instance.acquireTokenPopup({
        scopes: [`api://${authConfig.clientId}/access_as_user`],
      });
      return res.accessToken;
    }
  };

  const fetchVisits = useCallback(
    async (silent) => {
      if (!accounts.length) return;

      try {
        const token = await getAccessToken();
        if (!token) return;

        const url = new URL(`${getApiBaseUrl()}/visits`);
        const {
          page,
          sortBy,
          sortOrder,
          filterField,
          filterValue,
          hospitalFilter,
          labelFilter,
          statusFilter, 
          boardStatusFilter,
          expiringOnly,
          hideClean,
          open30plus,
        } = queryParams;

        url.searchParams.append('page', page);
        url.searchParams.append('limit', limit);
        if (sortBy) url.searchParams.append('sortBy', sortBy);
        if (sortOrder) url.searchParams.append('sortOrder', sortOrder);
        if (filterField && filterValue) {
          url.searchParams.append('filterField', filterField);
          url.searchParams.append('filterValue', filterValue);
        }

        hospitalFilter.forEach(h => url.searchParams.append('hospitalFilter', h));
        labelFilter.forEach(l => url.searchParams.append('labelFilter', l));
        statusFilter.forEach(s => url.searchParams.append('statusFilter', s));
        boardStatusFilter.forEach(s => url.searchParams.append('status', s));
        if (expiringOnly) url.searchParams.append('expiringOnly', true);
        if (hideClean) url.searchParams.append('hideClean', true);
        if (open30plus) url.searchParams.append('open30plus', true);

        const res = await fetch(url.toString(), {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) throw new Error('Visits fetch failed');

        const data = await res.json();
        
        const now = new Date();

        const next = (data.data || []).map(visit => {
          return {
            ...visit
          };
        });

        setVisitData(prev => {
          const prevMap = new Map(
            prev.map(v => [v.unique_id, getVisitSignature(v)])
          );

          let changed = prev.length !== next.length;

          if (!changed) {
            for (const visit of next) {
              if (prevMap.get(visit.unique_id) !== getVisitSignature(visit)) {
                changed = true;
                break;
              }
            }
          }

          if (changed) {
            if (silent) {
              logAutoRefresh('CHANGES_DETECTED', {
                previousCount: prev.length,
                newCount: next.length,
              });
            }
            return next;
          }

          if (silent) logAutoRefresh('NO_CHANGES');
          return prev;
        });

        setTotal(data.total || 0);
        setHospitalList(data.hospital_list || []);
        setLabelList(data.label_list || []);
        setBoardStatusList(data.status_list || []);
      } catch (e) {
        console.error('[AutoRefresh] Fetch failed', e);
      }
    },
    [accounts, queryParams, limit, getVisitSignature, logAutoRefresh]
  );


  useEffect(() => {
    fetchVisits(false);
  }, [queryParams, fetchVisits]);


  const fetchRef = useRef(fetchVisits);

  useEffect(() => {
    fetchRef.current = fetchVisits;
  }, [fetchVisits]);

  useEffect(() => {
    logAutoRefresh('POLL_START', { intervalMs: POLL_INTERVAL_MS });

    const id = setInterval(() => {
      logAutoRefresh('POLL_TICK');
      fetchRef.current(true);
    }, POLL_INTERVAL_MS);

    return () => {
      logAutoRefresh('POLL_STOP');
      clearInterval(id);
    };
  }, [logAutoRefresh]);

  const handleVisitClick = (visit) => {
    navigate(
      `/visit/${visit.unique_id}?${searchParams.toString()}`,
      {
        state: { visit },
      }
    );    
  };
  
  return (
      <>  
        <Subheader
          queryParams={queryParams}
          setQueryParams={setQueryParams}
          total={total}
          limit={limit}
          hospitalList={hospitalList}
        />

        <div className="card-grid">
          {visitData.map(v => (
            <VisitCard
              key={v.unique_id}
              visit={v}
              onClick={() => handleVisitClick(v)}
            />
          ))}
        </div>
      </>
  );
};

export default AllVisits;